import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GitBranch, CalendarClock, ShieldAlert, UserX, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import type { ProgramLeadQuickActionsData } from '@app-types';
import { conflictService } from '@services';
import styles from './ProgramLeadQuickActions.module.css';

interface ProgramLeadQuickActionsProps {
  data: ProgramLeadQuickActionsData | undefined;
  isLoading: boolean;
}

const ProgramLeadQuickActions: React.FC<ProgramLeadQuickActionsProps> = ({ data, isLoading }) => {
  const navigate = useNavigate();
  const missingRoleQuery = useQuery({
    queryKey: ['conflicts-worklist', 'MISSING_CONTRACT_ROLE', 'count'],
    queryFn: () =>
      conflictService.getWorklist({
        conflictTypeCode: 'MISSING_CONTRACT_ROLE',
        limit: 1,
        offset: 0
      })
  });

  const actions = [
    {
      label: 'Division Conflicts',
      count: data?.division_conflicts ?? 0,
      icon: GitBranch,
      iconVariant: styles.iconWarning,
      route: '/conflicts/division',
    },
    {
      label: 'Missing Contract Role',
      count: missingRoleQuery.data?.total ?? 0,
      icon: UserX,
      iconVariant: styles.iconWarning,
      route: '/conflicts/missing-contract-role',
    },
    {
      label: 'Meetings Pending Reschedule',
      count: data?.meetings_pending_reschedule ?? 0,
      icon: CalendarClock,
      iconVariant: styles.iconDanger,
      route: '/scheduler?myMeetings=false&status=scheduled',
    },
    {
      label: 'Escalated Assessments',
      count: data?.escalated_assessments ?? 0,
      icon: ShieldAlert,
      iconVariant: styles.iconInfo,
      route: '/assessments?status=Escalated',
    },
  ];

  if (isLoading) {
    return null;
  }

  return (
    <div className={styles.quickActionsContainer}>
      <div className={styles.actionsHeader}>
        <h3>Quick Actions</h3>
        <p>Exception items requiring your intervention</p>
      </div>
      <div className={styles.actionsGrid}>
        {actions.map((action) => {
          const IconComponent = action.icon;
          return (
            <button
              type="button"
              key={`action-${action.label}`}
              className={styles.actionTile}
              onClick={() => navigate(action.route)}
            >
              <div className={clsx(styles.actionIcon, action.iconVariant)}>
                <IconComponent size={24} />
              </div>
              <div className={styles.actionContent}>
                <p className={styles.actionLabel}>{action.label}</p>
                <p className={styles.actionCount}>{action.count}</p>
              </div>
              <ChevronRight size={20} className={styles.actionArrow} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { ProgramLeadQuickActions };
